import React, { useState, useRef, useEffect, useCallback } from "react";
import { FaTimes, FaExternalLinkAlt, FaExclamationTriangle, FaSyncAlt } from "react-icons/fa";
import { useMeetingAppContext } from "../../MeetingAppContextDef";
import { usePubSub } from "../../config/sdkConfig";
import useIsMobile from "../../hooks/useIsMobile";

const WebsiteSection = ({
  settings,
  onClose,
  canClose = true,
  sideBarContainerWidth,
  leftSidebarCollapsed = true,
  rightSidebarCollapsed = true,
  isRecorderMode = false,
}) => {
  const { sideBarMode } = useMeetingAppContext();
  const isMobile = useIsMobile();
  const [loadError, setLoadError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [retryKey, setRetryKey] = useState(0);
  const [iframeSrc, setIframeSrc] = useState(null);
  const iframeRef = useRef(null);

  const isStaticMode = settings.mode !== "interactive";
  const isPresenter = canClose;
  const apiBase = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BASE_URL || '';

  // Refs for stable access in callbacks (avoid stale closures in PubSub)
  const isPresenterRef = useRef(isPresenter);
  const isStaticModeRef = useRef(isStaticMode);
  useEffect(() => { isPresenterRef.current = isPresenter; }, [isPresenter]);
  useEffect(() => { isStaticModeRef.current = isStaticMode; }, [isStaticMode]);

  // --- PubSub: relay scroll between presenter and attendees ---
  const { publish: publishScroll } = usePubSub("OVERLAY_SCROLL", {
    onMessageReceived: (data) => {
      if (isPresenterRef.current || !isStaticModeRef.current) return;
      try {
        const msg = typeof data.message === 'string' ? JSON.parse(data.message) : data.message;
        if (msg?.scrollTop !== undefined && iframeRef.current?.contentWindow) {
          iframeRef.current.contentWindow.postMessage({
            type: 'rlink-scroll-to',
            scrollTop: msg.scrollTop,
            scrollLeft: msg.scrollLeft || 0,
          }, '*');
        }
      } catch (e) {}
    },
  });

  // Keep a ref so the postMessage handler always has the latest publish function
  const publishScrollRef = useRef(publishScroll);
  useEffect(() => { publishScrollRef.current = publishScroll; }, [publishScroll]);

  // --- Listen for postMessage from iframe ---
  useEffect(() => {
    if (!isStaticMode) return;

    const handleMessage = (e) => {
      if (!e.data?.type) return;
      // Presenter: relay scroll updates to attendees via PubSub
      if (e.data.type === 'rlink-scroll-update' && isPresenterRef.current) {
        const pub = publishScrollRef.current;
        if (pub) {
          pub(JSON.stringify({
            scrollTop: e.data.scrollTop,
            scrollLeft: e.data.scrollLeft,
          }));
        }
      }

      // Attendee: when iframe script is ready, send the lock command
      if (e.data.type === 'rlink-scroll-ready' && !isPresenterRef.current) {
        if (iframeRef.current?.contentWindow) {
          iframeRef.current.contentWindow.postMessage({ type: 'rlink-lock-scroll', lock: true }, '*');
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isStaticMode]);

  // --- On iframe load: lock scroll for attendees ---
  const handleLoad = useCallback(() => {
    setIsLoading(false);
    setLoadError(false);

    if (isStaticMode && !isPresenter && iframeRef.current?.contentWindow) {
      // Tell attendee's iframe to lock scrolling
      iframeRef.current.contentWindow.postMessage({ type: 'rlink-lock-scroll', lock: true }, '*');
    }
  }, [isStaticMode, isPresenter]);

  const getDirectUrl = useCallback(() => {
    const link = settings.link || '';
    if (link.startsWith('http://') || link.startsWith('https://')) return link;
    return 'https://' + link;
  }, [settings.link]);

  // Proxy URLs
  const getViewUrl = useCallback(() => {
    return `${apiBase}/proxy/view?url=${encodeURIComponent(getDirectUrl())}`;
  }, [getDirectUrl, apiBase]);

  const getEmbedUrl = useCallback(() => {
    return `${apiBase}/proxy/embed?url=${encodeURIComponent(getDirectUrl())}`;
  }, [getDirectUrl, apiBase]);

  // --- Loading logic ---
  // Static mode: /proxy/view (lightweight, scroll script)
  // Interactive mode: check if site allows embedding, if not use /proxy/embed
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setLoadError(false);

    if (isStaticMode) {
      setIframeSrc(getViewUrl());
    } else {
      // Check if site can be embedded directly (X-Frame-Options, CSP)
      const checkUrl = `${apiBase}/proxy/check-embed?url=${encodeURIComponent(getDirectUrl())}`;
      fetch(checkUrl)
        .then(res => res.json())
        .then(data => {
          if (cancelled) return;
          if (data.canEmbed) {
            // Site allows direct embedding
            setIframeSrc(getDirectUrl());
          } else {
            // Site blocks iframes — use proxy to strip headers
            setIframeSrc(getEmbedUrl());
          }
        })
        .catch(() => {
          if (cancelled) return;
          // Check failed — try direct, hope for the best
          setIframeSrc(getDirectUrl());
        });
    }

    return () => { cancelled = true; };
  }, [getDirectUrl, getViewUrl, getEmbedUrl, apiBase, retryKey, isStaticMode]);

  const handleError = () => {
    // Interactive mode: if direct load failed, try proxy (strips X-Frame-Options)
    if (!isStaticMode && iframeSrc && !iframeSrc.includes('/proxy/')) {
      setIframeSrc(getEmbedUrl());
    } else if (isStaticMode && iframeSrc?.includes('/proxy/view')) {
      // Static proxy failed, try direct
      setIframeSrc(getDirectUrl());
    } else {
      setIsLoading(false);
      setLoadError(true);
    }
  };

  const handleRefresh = () => {
    setIsLoading(true);
    setLoadError(false);
    setIframeSrc(null);
    setRetryKey(k => k + 1);
  };

  const leftSidebarWidth = isRecorderMode ? 0 : (leftSidebarCollapsed ? 56 : 380);
  const rightSidebarWidth = isRecorderMode ? 0 : (rightSidebarCollapsed ? 56 : 300);

  const getPositionStyles = () => {
    const position = settings.position || 'fullscreen';
    const pad = isRecorderMode ? 0 : 12;
    const fromLeft = leftSidebarWidth + pad;
    const fromRight = rightSidebarWidth + (sideBarMode ? sideBarContainerWidth : 0) + pad;
    const fromTop = (isRecorderMode ? 0 : 64) + pad;
    const fromBottom = (isRecorderMode ? 0 : 64) + pad;
    const base = { zIndex: 25, backgroundColor: '#000', borderRadius: '10px', overflow: 'hidden' };
    if (isMobile) return { ...base, top: 0, bottom: 0, left: 0, right: 0, borderRadius: 0, zIndex: 50 };
    switch (position) {
      case 'side_left': return { ...base, top: `${fromTop}px`, bottom: `${fromBottom}px`, left: `${fromLeft}px`, width: '35%', maxWidth: '450px' };
      case 'side_right': return { ...base, top: `${fromTop}px`, bottom: `${fromBottom}px`, right: `${fromRight}px`, width: '35%', maxWidth: '450px' };
      case 'top': return { ...base, top: `${fromTop}px`, left: `${fromLeft}px`, right: `${fromRight}px`, height: '35%', maxHeight: '350px' };
      case 'bottom': return { ...base, bottom: `${fromBottom}px`, left: `${fromLeft}px`, right: `${fromRight}px`, height: '35%', maxHeight: '350px' };
      default: return { ...base, top: `${fromTop}px`, bottom: `${fromBottom}px`, left: `${fromLeft}px`, right: `${fromRight}px` };
    }
  };

  return (
    <div onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()} className="fixed" style={getPositionStyles()}>
      {canClose && (
        <div style={{ position: "absolute", top: isMobile ? 12 : 8, right: isMobile ? 12 : 8, zIndex: 60, display: "flex", gap: 8 }}>
          {!isLoading && (
            <button onClick={(e) => { e.stopPropagation(); handleRefresh(); }}
              style={{ background: "rgba(0,0,0,0.7)", border: "none", borderRadius: "50%", width: isMobile ? 44 : 36, height: isMobile ? 44 : 36, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", cursor: "pointer" }}
              aria-label="Refresh" title="Refresh">
              <FaSyncAlt size={isMobile ? 16 : 14} />
            </button>
          )}
          <a href={getDirectUrl()} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
            style={{ background: "rgba(0,0,0,0.7)", border: "none", borderRadius: "50%", width: isMobile ? 44 : 36, height: isMobile ? 44 : 36, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", cursor: "pointer", textDecoration: "none" }}
            aria-label="Open in new tab" title="Open in new tab">
            <FaExternalLinkAlt size={isMobile ? 16 : 14} />
          </a>
          <button onClick={(e) => { e.stopPropagation(); onClose(); }}
            style={{ background: "rgba(220,38,38,0.85)", border: "none", borderRadius: "50%", width: isMobile ? 44 : 36, height: isMobile ? 44 : 36, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", cursor: "pointer" }}
            aria-label="Close website">
            <FaTimes size={isMobile ? 22 : 20} />
          </button>
        </div>
      )}

      <div className="absolute top-0 left-0 z-10 px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-[#2a2a2a]/90">
        {settings.mode === "interactive" ? "INTERACTIVE" : "STATIC"}
      </div>

      {loadError ? (
        <div className="w-full h-full flex flex-col items-center justify-center bg-[#1a1a2e] text-white p-6">
          <FaExclamationTriangle className="text-yellow-500 text-4xl mb-4" />
          <h3 className="text-xl font-bold mb-2">Could Not Load Website</h3>
          <p className="text-gray-400 text-center mb-4 max-w-md">This website could not be loaded in the overlay.</p>
          <div className="flex gap-3">
            <button onClick={handleRefresh} className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors">
              <FaSyncAlt /> Retry
            </button>
            <a href={getDirectUrl()} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-[#6a1fbf] hover:bg-[#5a1aaf] rounded-lg transition-colors">
              <FaExternalLinkAlt /> Open in New Tab
            </a>
          </div>
        </div>
      ) : (
        <>
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-[#1a1a2e]" style={{ zIndex: 10 }}>
              <div className="flex flex-col items-center">
                <div className="w-8 h-8 border-2 border-[#6a1fbf] border-t-transparent rounded-full animate-spin mb-2"></div>
                <p className="text-gray-400 text-sm">Loading website...</p>
              </div>
            </div>
          )}
          {iframeSrc && (
            <iframe
              key={retryKey}
              ref={iframeRef}
              src={iframeSrc}
              className="w-full h-full"
              style={{
                border: "none",
                pointerEvents: "auto",
                opacity: isLoading ? 0 : 1,
                transition: 'opacity 0.3s ease',
                background: '#fff',
              }}
              title="Website View"
              onLoad={handleLoad}
              onError={handleError}
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-presentation allow-popups-to-escape-sandbox"
              referrerPolicy="no-referrer"
              allow="autoplay; encrypted-media; fullscreen"
            />
          )}
        </>
      )}
    </div>
  );
};

export default WebsiteSection;
